import React, { ReactElement, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import {
  LoaderFunctionArgs,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Space } from 'common/components/antd/Space/Space';
import { Table } from 'common/components/antd/Table/Table';
import { Tabs } from 'common/components/antd/Tabs/Tabs';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import {
  useGetCounterpartyParticipantsListQuery,
  useGetCounterpartyReconsDetailstQuery,
  useGetCounterpartyReconsListQuery,
  useGetReconParticipantsListQuery,
} from 'domains/reconciliation/queries/reconciliation.query';
import {
  EReconciliationParticipantType,
  EReconciliationStatus,
  IReconciliationEntity,
  IReconciliationGetParticipantsResponse,
  IReconciliationParticipant,
  TTablePaginationConfig,
} from 'domains/reconciliation/types/reconciliation.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { ERoutePathParams } from 'navigation/enums/rout-path-params.enum';
import { reconRecordAtom } from './atoms/details.atom';
import { EQueryParams, EReconParticipantsColumnKeys } from './enums/statistic.enums';
import StatisticReconciliationDetailsModal from './modals/details/details.modal';
import StatisticReconciliationPayModal from './modals/pay/pay.modal';
import { STableContainer } from './statistic.page.style';
import { getColumns, getDebtorColumns, getReconsColumns } from './tables/table.columns';
import { tabItems } from './tabs/statistic.tabs'

const StatisticPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeParams = useParams() as { status: EReconciliationStatus };
  const paramsStatus = routeParams.status || EReconciliationStatus.Calculated;
  const [status, setStatus] = useState<EReconciliationStatus>(paramsStatus);
  const [creditor, setCreditor] = useState<string | null>(null);
  const [debtor, setDebtor] = useState<string | null>(null);
  const [debtorName, setDebtorName] = useState<string>('');
  const [, setReconRecord] = useAtom(reconRecordAtom);
  const [payModalData, setPayModalData] = useState<IReconciliationEntity | {}>({});
  const [detailModalData, setDetailModalData] = useState<IReconciliationEntity | {}>({});
  const [paginationData, setPaginationData] = useState<TTablePaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [queryParams, setQueryParams] = useState({
    counterparty: '',
    record: '',
  })

  const columns = getColumns();
  const debtorColumns = getDebtorColumns();

  const {
    data: participantsListData,
    isFetching: isParticipantsListDataFetching,
    isError: isParticipantsListDataError,
    error: participantsListDataError,
  } = useGetReconParticipantsListQuery({
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      counterparty_role: EReconciliationParticipantType.Debtor,
      status: [paramsStatus as EReconciliationStatus],
    },
    {
      refetchOnMount: false,
    }
  );

  const data = participantsListData?.participants?.map((participant) => ({
    ...participant,
    status,
  }));

  const onStatusChange = (statusToChange: string): void => {
    setStatus(statusToChange as EReconciliationStatus);
    navigate(ROUTES_MAPPING.PROTECTED.RECONCILIATION_STATISTIC.replace(`${ERoutePathParams.Status}?`, statusToChange));
  };

  const handlePaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const paginationConfig = {
    style: {
      marginRight: 30,
    },
    showQuickJumper: true,
    showSizeChanger: true,
    total: participantsListData?.total,
    pageSize: paginationData.pageSize,
    current: paginationData.page,
    onChange: handlePaginationChange,
  };

  const {
    data: creditorsData,
    isFetching: isCreditorsDataFetching,
    isError: isCreditorDataError,
    error: creditorsDataError,
  } = useGetCounterpartyParticipantsListQuery(
    debtor as string,
    { counterparty_role: EReconciliationParticipantType.Creditor, status: [status] },
    {
      enabled: !!debtor,
    },
  );

  const onCreditorRowExpand = (expanded: boolean, record: IReconciliationParticipant): void => {
    if (!IS_BANK_FE) {
      setCreditor(expanded ? record.guid : null);
    } else if (record.guid !== undefined) {
      setDebtor(expanded ? record.guid : null);
      setCreditor(expanded ? ENV_COUNTERPARTY_GUID : null);
    }
  };

  const {
    data: innerReconsData,
    isFetching: isInnerReconsDataFetching,
    isError: isInnerReconsDataError,
    error: innerReconsDataError,
  } = useGetCounterpartyReconsListQuery(
    debtor as string,
    creditor as string,
    { counterparty_role: EReconciliationParticipantType.Creditor, status: [status] },
    { enabled: !!creditor },
  );

  const {
    data: modalDetailData,
    isError: isModalDetailDataError,
    error: modalDetailDataError,
  } = useGetCounterpartyReconsDetailstQuery(
    '',
    queryParams?.counterparty,
    {
      guid: [String(queryParams?.record)],
      counterparty_role: EReconciliationParticipantType.Creditor,
    },
    { enabled: !!queryParams?.record?.length},
  );

  const onDetailsClickHandler = (reconRecord: IReconciliationEntity): void => {
    const recordToSet = reconRecord;
    recordToSet.debtor.name = debtorName;
    setReconRecord(reconRecord);
    if (status === EReconciliationStatus.Calculated) setPayModalData(reconRecord);
    if (status === EReconciliationStatus.Confirmed) setDetailModalData(reconRecord);
  };

  useEffect(() => {
    const counterparty = searchParams.get(EQueryParams.Counterparty);
    const record = searchParams.get(EQueryParams.Record);

    setQueryParams({
      counterparty: counterparty || '',
      record: record || '',
    })

    if (modalDetailData?.recons) setDetailModalData(modalDetailData?.recons[0]);

  }, [document.location.search, modalDetailData]);

  const onCancelPayModalHandler = (): void => {
    setPayModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.RECONCILIATION_STATISTIC
      .replace(`${ERoutePathParams.Status}?`, EReconciliationStatus.Calculated));
  };

  const onCancelDetailModalHandler = (): void => {
    setDetailModalData({});
    setQueryParams({
      counterparty: '',
      record: '',
    })
    searchParams.delete(EQueryParams.Counterparty);
    searchParams.delete(EQueryParams.Record);
    navigate(ROUTES_MAPPING.PROTECTED.RECONCILIATION_STATISTIC
      .replace(`${ERoutePathParams.Status}?`, EReconciliationStatus.Confirmed));
  };

  const expandedCreditorRowRender = (): ReactElement => {
    const reconsColumns = getReconsColumns({ onDetailsClickHandler });

    return (
      <Table
        loading={isInnerReconsDataFetching}
        rowKey={EReconParticipantsColumnKeys.GUID}
        showHeader
        columns={reconsColumns}
        dataSource={innerReconsData?.recons || []}
        pagination={false}
      />
    );
  };

  const expandedRowRender = (): ReactElement => (
    <Table
      loading={isCreditorsDataFetching}
      rowKey={EReconParticipantsColumnKeys.GUID}
      columns={debtorColumns}
      dataSource={creditorsData?.participants || []}
      pagination={false}
      expandable={{
        expandedRowRender: expandedCreditorRowRender,
        onExpand: onCreditorRowExpand,
        columnWidth: 30
      }}
    />
  );

  const isError = isParticipantsListDataError || isCreditorDataError || isInnerReconsDataError ||
    isModalDetailDataError;
  const error = participantsListDataError || creditorsDataError || innerReconsDataError || modalDetailDataError;

  useEffect(() => {
    if (isError) console.error(error);
  }, [isError, error]);

  return (
    <STableContainer>
      <Space style={{ marginInline: 24 }}>
        <Tabs defaultActiveKey={paramsStatus} items={tabItems} onChange={onStatusChange} />
      </Space>
      { IS_BANK_FE
        ?
          <Table
            loading={isCreditorsDataFetching}
            rowKey={EReconParticipantsColumnKeys.GUID}
            dataSource={data || []}
            columns={debtorColumns}
            pagination={paginationConfig}
            expandable={{
              expandedRowRender: expandedCreditorRowRender,
              onExpand: onCreditorRowExpand,
              columnWidth: 30
            }}
          />
        :
          <Table
            rootClassName="statistic-table"
            tableLayout="fixed"
            loading={isParticipantsListDataFetching}
            pagination={paginationConfig}
            rowKey={EReconParticipantsColumnKeys.GUID}
            dataSource={data}
            columns={columns}
            expandable={{
              expandedRowRender,
              onExpand: (_, record: IReconciliationParticipant): void => {
                setDebtor(record ? record.guid : null);
                setDebtorName(record ? record.name : '');
              },
              indentSize: 0,
              columnWidth: 30
            }}
          />
      }
      <StatisticReconciliationPayModal
        isOpen={!!Object.keys(payModalData).length}
        onCancelPayModalHandler={onCancelPayModalHandler}
        data={payModalData as IReconciliationEntity}
      />
      <StatisticReconciliationDetailsModal
        isOpen={!!Object.keys(detailModalData).length}
        onCancelDetailModalHandler={onCancelDetailModalHandler}
        data={detailModalData as IReconciliationEntity}
      />
    </STableContainer>
  );
};

export const loader = async ({ request, params }: LoaderFunctionArgs): Promise<{
  participantsListData: IReconciliationGetParticipantsResponse | {},
}> => {
  try {
    const url = new URL(request.url);
    const searchParams = new URLSearchParams(url.search);
    const status =
      searchParams.get(EQueryParams.Status) || params[EQueryParams.Status] || EReconciliationStatus.Calculated;

    let participantsListData: IReconciliationGetParticipantsResponse;

    if (IS_BANK_FE) {
      const participantsQuery = queryKeysClient[EQueryKeys.ReconciliationQueryKeys].getCounterpartyParticipants(
        ENV_COUNTERPARTY_GUID,
        {
          offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
          limit: DEFAULT_PAGE_SIZE,
          counterparty_role: EReconciliationParticipantType.Debtor,
          status: [status as EReconciliationStatus],
        }
      );

      participantsListData =
        queryClient.getQueryData(participantsQuery.queryKey) ?? (await queryClient.fetchQuery(participantsQuery));
    } else {
       const participantsQuery = queryKeysClient[EQueryKeys.ReconciliationQueryKeys].getParticipants({
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
        counterparty_role: EReconciliationParticipantType.Debtor,
        status: [status as EReconciliationStatus],
      });

      participantsListData =
        queryClient.getQueryData(participantsQuery.queryKey) ?? (await queryClient.fetchQuery(participantsQuery));
    }

    return {
      participantsListData,
    };

  } catch (error) {
    console.error(error);

    return {
      participantsListData: {},
    }
  }
};

export default StatisticPage;
